import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import ProductCard from '../components/ProductCard';
import { getCmsCollection, getCmsPage } from '../lib/cms';

const FILTER_TAGS = [
  { id: 'alle', label: 'Alle produkter', icon: 'fa-paw' },
  { id: 'hvalp', label: 'Hvalp (2-20 mdr)', icon: 'fa-dog' },
  { id: 'aktiv', label: 'Aktiv & Sport', icon: 'fa-bolt' },
  { id: 'sensitiv', label: 'Sensitiv / Lam & Ris', icon: 'fa-shield-heart' },
  { id: 'alle-racer', label: 'Alle racer', icon: 'fa-wheat-awn' },
  { id: 'smaa-hunde', label: 'Små racer / Mini', icon: 'fa-bone' },
];

export default function DogFoodPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBehov = searchParams.get('behov') || 'alle';

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        setLoading(true);
        const [prodRes, pageData] = await Promise.all([
          getCmsCollection('produkter', {
            kategori: 'hund',
            sort: 'sorteringsorden',
            order: 'asc',
            limit: 50,
          }),
          getCmsPage('/hundefoder'),
        ]);

        if (isMounted) {
          setProducts(prodRes.items || []);
          if (pageData) setPage(pageData);
          setLoading(false);
        }
      } catch (err) {
        console.error('Kunne ikke hente produkter:', err);
        if (isMounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Filter products based on activeBehov tag and search query
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // 1. Tag filter
      if (activeBehov !== 'alle') {
        const terms = p.terms?.foderbehov || [];
        const hasTerm = terms.includes(activeBehov);
        const hasTagInProperties =
          (p.egenskaber && p.egenskaber.toLowerCase().includes(activeBehov.toLowerCase())) ||
          (p.produkttype && p.produkttype.toLowerCase().includes(activeBehov.toLowerCase())) ||
          (p.navn && p.navn.toLowerCase().includes(activeBehov.toLowerCase()));

        if (!hasTerm && !hasTagInProperties) {
          return false;
        }
      }

      // 2. Search query filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const matchName = p.navn && p.navn.toLowerCase().includes(query);
        const matchDesc = p.kort_beskrivelse && p.kort_beskrivelse.toLowerCase().includes(query);
        const matchType = p.produkttype && p.produkttype.toLowerCase().includes(query);
        if (!matchName && !matchDesc && !matchType) {
          return false;
        }
      }

      return true;
    });
  }, [products, activeBehov, searchQuery]);

  const handleFilterChange = (id) => {
    const nextParams = new URLSearchParams(searchParams);
    if (id === 'alle') {
      nextParams.delete('behov');
    } else {
      nextParams.set('behov', id);
    }
    setSearchParams(nextParams, { preventScrollReset: true });
  };

  return (
    <>
      <SeoHead
        title={page?.meta_title || "Hundefoder - KROMIX A/S"}
        description={page?.meta_description || "Velsmagende foder fyldt med energi! Fremstillet af nøje udvalgte kvalitetsråvarer til hvalpe, aktive hunde, sensitive hunde og alle racer."}
      />

      {/* Hero Header: Deep Moss statement section */}
      <section className="bg-[#39463A] text-[#FCFAF5] py-14 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-widest text-[#DED3C0] mb-2 block">
              {page?.badge_tekst || 'Sortimentsoversigt'}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#FCFAF5]">
              {page?.overskrift || 'Hundefoder'}
            </h1>
            <p className="text-base sm:text-lg text-[#FCFAF5]/90 mt-3 leading-relaxed">
              {page?.undertekst || 'Velsmagende foder fyldt med energi! Fremstillet af nøje udvalgte kvalitetsråvarer. Find det rette fuldfoder til din hunds aktivitetsniveau, alder og størrelse.'}
            </p>
          </div>
        </div>
      </section>

      {/* Catalog & Filter Section: Warm Oat background */}
      <section className="py-10 bg-[#F4EFE5] min-h-[600px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Control Bar: Filters & Search on Soft Ivory surface */}
          <div className="bg-[#FCFAF5] rounded-2xl p-4 sm:p-5 border border-[#DED3C0] shadow-xs mb-8 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Search input */}
              <div className="relative flex-1 max-w-md">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Søg i hundefoder (f.eks. Fiberkost, Junior, Minies)..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#F4EFE5] border border-[#DED3C0] text-sm text-[#39463A] placeholder-[#39463A]/60 focus:outline-hidden focus:border-[#71806A] focus:ring-1 focus:ring-[#71806A]"
                />
                <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-3.5 text-[#71806A] text-sm" aria-hidden="true" />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-3 text-xs text-[#39463A]/70 hover:text-[#39463A]"
                    aria-label="Ryd søgning"
                  >
                    <i className="fa-solid fa-circle-xmark" aria-hidden="true" />
                  </button>
                )}
              </div>

              {/* Status info */}
              <div className="flex items-center justify-between md:justify-end gap-3 text-xs text-[#39463A]/80">
                <span>
                  Viser <strong className="text-[#39463A]">{filteredProducts.length}</strong> produkter
                </span>
              </div>
            </div>

            {/* Category Filter Pills (Desktop & Tablet) */}
            <div className="pt-2 border-t border-[#DED3C0] flex flex-wrap gap-2">
              {FILTER_TAGS.map((tag) => {
                const isActive = activeBehov === tag.id;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleFilterChange(tag.id)}
                    className={`px-3.5 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer ${
                      isActive
                        ? 'bg-[#71806A] text-[#FCFAF5] shadow-xs'
                        : 'bg-[#F4EFE5] text-[#39463A] hover:bg-[#DED3C0]/40 border border-[#DED3C0]'
                    }`}
                  >
                    <i className={`fa-solid ${tag.icon} text-[11px] ${isActive ? 'text-[#FCFAF5]' : 'text-[#71806A]'}`} aria-hidden="true" />
                    <span>{tag.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Product Grid */}
          {loading ? (
            <div className="py-20 text-center text-sm text-[#39463A]/80">
              <i className="fa-solid fa-spinner fa-spin text-2xl text-[#71806A] mb-2 block" />
              Indlæser hundefodersortiment...
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="py-16 text-center bg-[#FCFAF5] rounded-2xl border border-[#DED3C0] p-8 max-w-lg mx-auto">
              <i className="fa-solid fa-paw text-3xl text-[#71806A] mb-3 block" aria-hidden="true" />
              <h3 className="text-base font-bold text-[#39463A]">Ingen produkter fundet</h3>
              <p className="text-xs text-[#39463A]/80 mt-1">
                Der blev ikke fundet nogle hundefoderprodukter, der matcher din søgning eller dit valgte filter.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSearchQuery('');
                  handleFilterChange('alle');
                }}
                className="mt-4 px-4 py-2 rounded-xl bg-[#71806A] text-[#FCFAF5] text-xs font-bold uppercase tracking-wider hover:bg-[#5C6A56]"
              >
                Nulstil filter
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((prod) => (
                <ProductCard key={prod.id} product={prod} />
              ))}
            </div>
          )}

          {/* Professional guidance notice at bottom */}
          <div className="mt-14 p-6 rounded-2xl bg-[#DED3C0]/40 border border-[#DED3C0] flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div className="space-y-1 max-w-2xl">
              <h3 className="text-base font-bold text-[#39463A]">
                Har du spørgsmål til valg af hundefoder?
              </h3>
              <p className="text-xs text-[#39463A]/80">
                Vi rådgiver gerne om det optimale foder til din hunds alder, aktivitetsniveau og særlige behov. Ring til os på 75 73 18 61 eller send en besked.
              </p>
            </div>
            <Link
              to="/kontakt"
              className="px-6 py-3 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-xs font-bold uppercase tracking-wider transition-all shadow-md shrink-0 whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope mr-1.5" aria-hidden="true" />
              Kontakt KROMIX
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
